import {
  TENANT_MODULE_LABELS,
  type TenantModuleKey,
  type TenantModules,
} from "@loot-os/api-contract";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { Switch } from "@loot-os/ui/components/switch";

import { TENANT_MODULE_FORM } from "../lib/tenantModulesForm";

/**
 * One switch per module, the storefront's own indented beneath it and disabled while it is off
 * (the server treats them as off then, whatever they say). Controlled: the caller holds the map.
 */
export function TenantModulesFields({
  idPrefix,
  value,
  onChange,
  disabled = false,
}: {
  idPrefix: string;
  value: TenantModules;
  onChange: (next: TenantModules) => void;
  disabled?: boolean;
}) {
  const row = (key: TenantModuleKey, nested: boolean, rowDisabled: boolean) => (
    <div key={key} className={`flex items-center justify-between gap-3 ${nested ? "pl-6" : ""}`}>
      <Label
        htmlFor={`${idPrefix}-${key}`}
        className={nested ? "font-normal text-muted-foreground" : undefined}
      >
        {TENANT_MODULE_LABELS[key]}
      </Label>
      <Switch
        id={`${idPrefix}-${key}`}
        checked={value[key] && !rowDisabled}
        disabled={disabled || rowDisabled}
        onCheckedChange={(checked) => onChange({ ...value, [key]: checked })}
      />
    </div>
  );
  return (
    <div className="grid gap-2">
      {TENANT_MODULE_FORM.map(({ key, children }) => (
        <div key={key} className="grid gap-2">
          {row(key, false, false)}
          {children.map((child) => row(child, true, !value[key]))}
        </div>
      ))}
    </div>
  );
}

/** The two shares as percent inputs; the caller holds the text and parses it. */
export function CommercialTermsFields({
  idPrefix,
  storefrontProfitShare,
  shopifyPlatformShare,
  onStorefrontProfitShareChange,
  onShopifyPlatformShareChange,
  disabled = false,
}: {
  idPrefix: string;
  storefrontProfitShare: string;
  shopifyPlatformShare: string;
  onStorefrontProfitShareChange: (text: string) => void;
  onShopifyPlatformShareChange: (text: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="grid gap-2">
        <Label htmlFor={`${idPrefix}-storefront-share`}>Storefront profit share (%)</Label>
        <Input
          id={`${idPrefix}-storefront-share`}
          inputMode="decimal"
          value={storefrontProfitShare}
          onChange={(e) => onStorefrontProfitShareChange(e.target.value)}
          placeholder="0"
          disabled={disabled}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor={`${idPrefix}-shopify-share`}>Shopify platform (%)</Label>
        <Input
          id={`${idPrefix}-shopify-share`}
          inputMode="decimal"
          value={shopifyPlatformShare}
          onChange={(e) => onShopifyPlatformShareChange(e.target.value)}
          placeholder="0"
          disabled={disabled}
        />
      </div>
    </div>
  );
}
